import React, { useState, useEffect, useRef } from 'react';
import Particles from './components/Particles';

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const menuItems = [
    { id: 'host', label: 'Host' },
    { id: 'evento', label: 'Evento' },
    { id: 'invitados-especiales', label: 'Invitados' },
    { id: 'cosplay', label: 'Cosplay' },
    { id: 'sponsors', label: 'Sponsors' },
    { id: 'registro', label: 'Registro' }
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-black/90 backdrop-blur-lg border-b border-violet-500/50 shadow-lg shadow-violet-500/20' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
        {/* Logo */}
        <div className="cursor-pointer transition-transform duration-300 hover:scale-105" onClick={() => scrollTo('hero')}>
          <img
            src="/thot-logo.png"
            alt="THOT 15 Años"
            className="h-12 md:h-16 w-auto drop-shadow-lg"
          />
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex space-x-8">
          {menuItems.map(item => (
            <button 
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-white font-medium uppercase tracking-wider hover:text-[#6bc2e2] transition-all duration-300 relative group py-2"
            >
              {item.label}
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] group-hover:w-full transition-all duration-300"></span>
              <span className="absolute top-0 left-0 w-0 h-0.5 bg-gradient-to-r from-[#a126e9] to-[#ed3cb2] group-hover:w-full transition-all duration-300 delay-100"></span>
            </button>
          ))}
        </div>

        {/* Mobile Menu Button */}
        <button 
          className="md:hidden text-white focus:outline-none relative w-6 h-6"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          <span className={`block absolute h-0.5 w-6 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] transform transition duration-300 ease-in-out ${isMobileMenuOpen ? 'rotate-45 translate-y-2' : 'translate-y-1'}`}></span>
          <span className={`block absolute h-0.5 w-6 bg-gradient-to-r from-[#a126e9] to-[#ed3cb2] transform transition duration-300 ease-in-out translate-y-2 ${isMobileMenuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`block absolute h-0.5 w-6 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] transform transition duration-300 ease-in-out ${isMobileMenuOpen ? '-rotate-45 translate-y-2' : 'translate-y-3'}`}></span>
        </button>
      </div>

      {/* Mobile Menu */}
      <div className={`md:hidden bg-black/95 backdrop-blur-lg border-b border-pink-500/30 transition-all duration-300 overflow-hidden ${
        isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="px-6 py-4 space-y-4">
          {menuItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="block w-full text-left text-white font-medium uppercase tracking-wider hover:text-[#6bc2e2] transition-all duration-300 py-2 border-l-2 border-transparent hover:border-[#6bc2e2] pl-4"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
};

// Countdown Component
const Countdown = () => {
  const [timeLeft, setTimeLeft] = useState({
    days: 45,
    hours: 12,
    minutes: 30,
    seconds: 15
  });

  useEffect(() => {
    const timer = setInterval(() => {
      // Fecha del evento: 16 de noviembre de 2025 a las 12:00 del mediodía
      const eventDate = new Date('2025-11-16T12:00:00');
      
      const now = new Date();
      const diff = eventDate - now;
      
      // Si el evento ya pasó, mostrar ceros
      if (diff <= 0) {
        setTimeLeft({
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        });
      } else {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex justify-center gap-4 md:gap-8 my-8">
      {Object.entries(timeLeft).map(([unit, value]) => (
        <div key={unit} className="text-center bg-[#a126e9]/10 border border-[#a126e9] rounded-xl p-4 min-w-[80px] backdrop-blur-sm">
          <div className="text-2xl md:text-4xl font-bold text-[#6bc2e2]">{value}</div>
          <div className="text-xs md:text-sm text-gray-300 uppercase mt-1">
            {unit === 'days' ? 'Días' : unit === 'hours' ? 'Horas' : unit === 'minutes' ? 'Min' : 'Seg'}
          </div>
        </div>
      ))}
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, delay = 0, gradient = "from-pink-500/20 to-cyan-400/20" }) => {
  return (
    <div 
      className="bg-white/5 backdrop-blur-lg border border-pink-500/30 rounded-2xl p-6 transition-all duration-500 hover:transform hover:-translate-y-3 hover:border-cyan-400 hover:shadow-2xl hover:shadow-pink-500/20 group relative overflow-hidden"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Animated background gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-50 transition-opacity duration-500`}></div>
      
      {/* Corner accents */}
      <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-[#6bc2e2]/50 group-hover:border-[#6bc2e2] transition-colors duration-300"></div>
      <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-[#ed3cb2]/50 group-hover:border-[#ed3cb2] transition-colors duration-300"></div>
      
      <div className="relative z-10">
        <div className="text-5xl mb-6 text-[#6bc2e2] group-hover:scale-110 group-hover:text-[#ed3cb2] transition-all duration-300 text-center">
          {icon}
        </div>
        <h3 className="text-xl font-bold text-[#6bc2e2] uppercase mb-4 tracking-wide group-hover:text-[#ed3cb2] transition-colors duration-300">
          {title}
        </h3>
        <p className="text-gray-300 leading-relaxed group-hover:text-gray-100 transition-colors duration-300">
          {description}
        </p>
      </div>
      
      {/* Shimmer effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
    </div>
  );
};


// Hero Section Component
const HeroSection = ({ onOpenModal }) => {
  return (
    <section id="hero" className="min-h-screen flex flex-col justify-center items-center text-center relative z-10 px-6 pt-24 md:pt-0">
      {/* Enhanced scanning lines effect - Solo en los extremos */}
      <div className="absolute top-24 md:top-24 w-full h-0.5 bg-gradient-to-r from-transparent via-[#6bc2e2] to-transparent animate-pulse hidden md:block"></div>
      <div className="absolute bottom-10 w-full h-0.5 bg-gradient-to-r from-transparent via-[#ed3cb2]/50 to-transparent animate-pulse hidden md:block" style={{animationDelay: '2s'}}></div>

      {/* Glowing corners - Ocultos en mobile */}
      <div className="absolute top-24 left-10 w-20 h-20 border-l-2 border-t-2 border-[#6bc2e2] opacity-50 hidden md:block"></div>
      <div className="absolute top-24 right-10 w-20 h-20 border-r-2 border-t-2 border-[#ed3cb2] opacity-50 hidden md:block"></div>
      <div className="absolute bottom-10 left-10 w-20 h-20 border-l-2 border-b-2 border-[#a126e9] opacity-50 hidden md:block"></div>
      <div className="absolute bottom-10 right-10 w-20 h-20 border-r-2 border-b-2 border-[#6bc2e2] opacity-50 hidden md:block"></div>

      <div className="inline-block px-8 py-3 border-2 border-pink-500 rounded-full bg-pink-500/10 mb-4 animate-pulse backdrop-blur-sm relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
        <span className="text-lg font-semibold uppercase tracking-wider relative z-10">15º Aniversario</span>
      </div>

      <div className="relative mb-6 animate-title-glow">
        {/* Imagen principal épica */}
        <img
          src="/thot-logo.png"
          alt="THOT 15 AÑOS - Celebrando 15 años de innovación"
          className="w-auto h-32 md:h-48 lg:h-64 xl:h-80 mx-auto relative z-20 drop-shadow-2xl"
        />

        {/* Sombra épica para profundidad */}
        <img
          src="/thot-logo.png"
          alt=""
          className="absolute inset-0 w-auto h-32 md:h-48 lg:h-64 xl:h-80 mx-auto opacity-40 transform translate-x-2 translate-y-2 blur-sm"
        />
      </div>

      <p className="text-xl md:text-3xl text-gray-300 mb-8 max-w-3xl font-light leading-relaxed">
        ¡El evento gamer más grande del país!
        <br />
        <span className="text-lg md:text-xl text-[#6bc2e2] font-medium">Centro de eventos del LATU | de 13 a 20 horas.</span>
      </p>

      <Countdown />

      <div className="flex flex-col md:flex-row gap-4 mt-6">
        <a
          href="https://redtickets.uy/evento/THOT-15-ANOS/23852/"
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            if (window.fbq) {
              window.fbq('track', 'ComprarEntrada');
            }
          }}
          className="px-10 py-5 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] text-white font-bold uppercase tracking-wider rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ed3cb2]/50 relative overflow-hidden group"
        >
          <span className="absolute inset-0 bg-gradient-to-r from-[#a126e9] to-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
          <span className="relative z-10">Comprar Entradas</span>
        </a>
      </div>
    </section>
  );
};

// Combined Special Guests Section - MORTE, FALKE, JOHNNY & URUGOD
const SpecialGuestsSection = () => {
  const specialGuests = [
    {
      name: "MORTE",
      type: "Streamer Argentino",
      description: "Uno de los streamers más influyentes de Argentina con una comunidad masiva",
      followers: "2.8M+",
      platform: "Twitch",
      image: "/Morte.avif",
      social: "@Elmortedor",
      badge: "⭐ Invitado Especial ⭐",
      badgeColor: "ed3cb2",
      mainColor: "ed3cb2"
    },
    {
      name: "FALKE",
      type: "Artista & Trapero",
      description: "Reconocido en la escena del trap. ¡Presentación exclusiva en vivo!",
      followers: "1.5M+",
      platform: "Instagram",
      image: "/Falke.jpg",
      social: "@Falke.912",
      badge: "🎤 Show en Vivo 🎤",
      badgeColor: "a126e9",
      mainColor: "a126e9"
    },
    {
      name: "JOHNNY",
      type: "Influencer Uruguayo",
      description: "Influencer uruguayo reconocido por sus experimentos gastronómicos",
      followers: "",
      platform: "",
      image: "/johnny.jpg",
      social: "@JohnnyTecuento",
      badge: "🎙️ HOST DEL EVENTO 🎙️",
      badgeColor: "6bc2e2",
      mainColor: "6bc2e2"
    },
    {
      name: "URUGOD",
      type: "Streamer Uruguayo",
      description: "Streamer uruguayo con una gran comunidad que lo acompaña en sus locuras",
      followers: "",
      platform: "",
      image: "/urugod.jpg",
      social: "@soyurugodd",
      badge: "📺 Stream en Vivo 📺",
      badgeColor: "ed3cb2",
      mainColor: "ed3cb2"
    }
  ];

  return (
    <section id="invitados-especiales" className="py-20 px-6 relative z-10 bg-gradient-to-b from-[#a126e9]/5 to-transparent">
      <div className="max-w-6xl mx-auto">
        {/* Espaciado superior */}
        <div className="mb-8"></div>
        
        {/* Cards de invitados - 2x2 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-7xl mx-auto">
          {specialGuests.map((guest, index) => (
            <div key={index} className="flex flex-col">
              {/* Badge individual para cada invitado - MÁS GRANDE */}
              <div className="text-center mb-8">
                <div className={`inline-block px-10 py-5 border border-[#${guest.badgeColor}]/60 rounded-full bg-[#${guest.badgeColor}]/30 backdrop-blur-sm`}>
                  <span className="text-white font-bold uppercase tracking-wider drop-shadow-lg text-xl">
                    {guest.badge}
                  </span>
                </div>
              </div>

              {/* Card del invitado - MÁS GRANDE */}
              <div className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-xl border border-[#${guest.mainColor}]/50 rounded-3xl p-12 transition-all duration-500 hover:transform hover:-translate-y-4 hover:border-[#${guest.mainColor}] hover:shadow-2xl hover:shadow-[#${guest.mainColor}]/40 flex-1 min-h-[600px]`}>
                {/* Efecto de resplandor */}
                <div className={`absolute inset-0 bg-gradient-to-br from-[#${guest.mainColor}]/30 via-[#6bc2e2]/15 to-[#${guest.mainColor === 'ed3cb2' ? 'a126e9' : 'ed3cb2'}]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl`}></div>
                
                {/* Contenido */}
                <div className="relative z-10 text-center">
                  {/* Imagen - MÁS GRANDE */}
                  <div className="mb-8">
                    <div className="relative inline-block">
                      <img 
                        src={guest.image} 
                        alt={`${guest.name} - ${guest.type}`}
                        className={`w-72 h-48 md:w-80 md:h-52 object-cover rounded-2xl border-4 border-[#${guest.mainColor}] group-hover:border-[#6bc2e2] transition-all duration-300 group-hover:scale-105 shadow-2xl mx-auto`}
                      />
                      {/* Marco de resplandor */}
                      <div className={`absolute inset-0 rounded-2xl border-2 border-[#${guest.mainColor}]/60 animate-pulse scale-105`}></div>
                      {/* Overlay gradient */}
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/40 to-transparent rounded-2xl group-hover:from-[#${guest.mainColor}]/20 transition-all duration-300`}></div>
                    </div>
                  </div>

                  {/* Información */}
                  <div className="space-y-4">
                    <h3 className={`text-5xl md:text-7xl font-black text-[#${guest.mainColor}] uppercase tracking-wider group-hover:text-[#6bc2e2] transition-colors duration-300`}>
                      {guest.name}
                    </h3>
                    
                    <div className={`bg-[#${guest.mainColor}]/40 border border-[#${guest.mainColor}]/60 rounded-full px-6 py-3 inline-block backdrop-blur-sm`}>
                      <span className="text-white font-bold text-base uppercase tracking-wide drop-shadow-lg">
                        {guest.type}
                      </span>
                    </div>
                    
                    <p className="text-gray-300 text-base leading-relaxed max-w-sm mx-auto">
                      {guest.description}
                    </p>

                    {/* Stats - MÁS GRANDES */}
                    <div className="flex justify-center items-center pt-6">
                      <div className="text-center">
                        <div className={`text-xl font-bold text-[#${guest.mainColor}]`}>{guest.social}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Efectos decorativos */}
                <div className={`absolute top-4 right-4 w-3 h-3 bg-[#${guest.mainColor}] rounded-full animate-pulse`}></div>
                <div className="absolute bottom-4 left-4 w-2 h-2 bg-[#6bc2e2] rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to action conjunto */}
        <div className="text-center mt-12">
          <div className="inline-block bg-gradient-to-r from-[#ed3cb2]/20 to-[#a126e9]/20 rounded-2xl p-6 backdrop-blur-sm border border-[#6bc2e2]/30">
            <p className="text-[#6bc2e2] font-bold text-lg mb-2">🎮 ¡Cuatro experiencias únicas en un solo evento! 🎤</p>
            <p className="text-gray-300 text-sm">Invitados especiales, show en vivo, streams y tu host favorito</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section Component
const FeaturesSection = () => {
  const features = [
    {
      icon: "🏆",
      title: "Finales Épicas",
      description: "Las grandes finales de Valorant, CS2 y League of Legends con premios increíbles."
    },
    {
      icon: "🌟",
      title: "Invitados Especiales",
      description: "¡Conocé a tus creadores favoritos en persona y terminá el evento con un show en vivo!."
    },
    {
      icon: "🎁",
      title: "Sorteos Épicos",
      description: "Se sorteará una PC Gamer, periféricos de última generación, componentes y un montón de sorpresas!."
    },
    {
      icon: "⚡",
      title: "Tech Zone",
      description: "Probá la tecnología más avanzada y hardware gaming de próxima generación."
    },
    {
      icon: "🎮",
      title: "Gaming Stations",
      description: "Más de 30 estaciones gaming con los títulos más recientes y clásicos retro."
    },
    {
      icon: "🚀",
      title: "Innovación VR/IA",
      description: "Vení a probar y viví lo más avanzado en tecnología emergente."
    },
    {
      icon: "🍔",
      title: "Food Trucks",
      description: "Tus juegos favoritos acompañados de tu comida favorita ;)"
    },
    {
      icon: "😌",
      title: "Zona Chill",
      description: "Para que puedas descansar, recargar energías y seguir jugando!"
    },
    {
      icon: "🎭",
      title: "Concurso de Cosplay",
      description: "Mirá a tus cosplayers favoritos realizar presentaciones únicas en nuestro concurso de cosplay"
    }
  ];

  return (
    <section id="evento" className="py-20 px-6 relative z-10">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] bg-clip-text text-transparent uppercase">
          ¡Experiencia Gamer única!
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => {
            const gradients = [
              "from-[#ed3cb2]/20 to-[#a126e9]/20",
              "from-[#a126e9]/20 to-[#6bc2e2]/20",
              "from-[#6bc2e2]/20 to-[#ed3cb2]/20",
              "from-[#ed3cb2]/20 to-[#a126e9]/20",
              "from-[#a126e9]/20 to-[#6bc2e2]/20",
              "from-[#6bc2e2]/20 to-[#ed3cb2]/20",
              "from-[#ed3cb2]/20 to-[#6bc2e2]/20",
              "from-[#a126e9]/20 to-[#ed3cb2]/20",
              "from-[#6bc2e2]/20 to-[#a126e9]/20"
            ];

            return (
              <div key={index}>
                <FeatureCard
                  icon={feature.icon}
                  title={feature.title}
                  description={feature.description}
                  delay={index * 100}
                  gradient={gradients[index % gradients.length]}
                />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};


// Zona de Experiencia Section Component
const ZonaExperienciaSection = () => {
  const torneos = [
    { icon: "🎯", title: "VALORANT 1VS1" },
    { icon: "🏗️", title: "FORTNITE 1VS1" },
    { icon: "🚗", title: "ROCKET LEAGUE 1VS1" },
    { icon: "⚽", title: "FC26" },
    { icon: "🥊", title: "MORTAL KOMBAT" },
    { icon: "⛏️", title: "MINECRAFT" }
  ];

  return (
    <section id="zona-experiencia" className="py-20 px-6 relative z-10 bg-gradient-to-b from-[#6bc2e2]/5 to-transparent">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-4 bg-gradient-to-r from-[#6bc2e2] to-[#a126e9] bg-clip-text text-transparent uppercase">
          Zona de Experiencia
        </h2>

        {/* Subtitle */}
        <div className="text-center mb-12">
          <div className="inline-block px-8 py-3 border-2 border-[#6bc2e2]/60 rounded-full bg-[#6bc2e2]/20 backdrop-blur-sm">
            <span className="text-[#6bc2e2] font-bold uppercase tracking-wider text-lg">⚡ Torneos Flash ⚡</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {torneos.map((torneo, index) => {
            const colors = ['#6bc2e2', '#a126e9', '#ed3cb2'];
            const currentColor = colors[index % colors.length];
            const gradients = [
              "from-[#6bc2e2]/20 to-[#a126e9]/20",
              "from-[#a126e9]/20 to-[#ed3cb2]/20",
              "from-[#ed3cb2]/20 to-[#6bc2e2]/20"
            ];

            return (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-pink-500/30 rounded-2xl p-6 transition-all duration-500 hover:transform hover:-translate-y-3 hover:border-cyan-400 hover:shadow-2xl hover:shadow-pink-500/20 group relative overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Animated background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index % gradients.length]} opacity-0 group-hover:opacity-50 transition-opacity duration-500`}></div>

                {/* Corner accents */}
                <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-[#6bc2e2]/50 group-hover:border-[#6bc2e2] transition-colors duration-300"></div>
                <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-[#ed3cb2]/50 group-hover:border-[#ed3cb2] transition-colors duration-300"></div>

                <div className="relative z-10">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-all duration-300 text-center"
                    style={{ color: currentColor }}>
                    {torneo.icon}
                  </div>
                  <h3 className="text-xl font-bold uppercase mb-4 tracking-wide transition-colors duration-300 text-center"
                    style={{ color: currentColor }}>
                    {torneo.title}
                  </h3>
                </div>

                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Desafíos Section Component
const DesafiosSection = () => {
  const desafios = [
    { icon: "🐻", title: "Five Nights at Freddy's" },
    { icon: "🏎️", title: "Assetto Corsa" },
    { icon: "💃", title: "Just Dance" },
    { icon: "✨", title: "¡Y mucho más!" }
  ];

  return (
    <section id="desafios" className="py-20 px-6 relative z-10 bg-gradient-to-b from-transparent to-[#a126e9]/5">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] bg-clip-text text-transparent uppercase">
          Desafíos
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {desafios.map((desafio, index) => {
            const colors = ['#ed3cb2', '#a126e9', '#6bc2e2', '#ed3cb2'];
            const currentColor = colors[index % colors.length];
            const gradients = [
              "from-[#ed3cb2]/20 to-[#a126e9]/20",
              "from-[#a126e9]/20 to-[#6bc2e2]/20",
              "from-[#6bc2e2]/20 to-[#ed3cb2]/20",
              "from-[#ed3cb2]/20 to-[#a126e9]/20"
            ];

            return (
              <div
                key={index}
                className="bg-white/5 backdrop-blur-lg border border-pink-500/30 rounded-2xl p-6 transition-all duration-500 hover:transform hover:-translate-y-3 hover:border-cyan-400 hover:shadow-2xl hover:shadow-pink-500/20 group relative overflow-hidden"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {/* Animated background gradient */}
                <div className={`absolute inset-0 bg-gradient-to-br ${gradients[index % gradients.length]} opacity-0 group-hover:opacity-50 transition-opacity duration-500`}></div>

                {/* Corner accents */}
                <div className="absolute top-0 right-0 w-6 h-6 border-r-2 border-t-2 border-[#6bc2e2]/50 group-hover:border-[#6bc2e2] transition-colors duration-300"></div>
                <div className="absolute bottom-0 left-0 w-6 h-6 border-l-2 border-b-2 border-[#ed3cb2]/50 group-hover:border-[#ed3cb2] transition-colors duration-300"></div>

                <div className="relative z-10">
                  <div className="text-5xl mb-6 group-hover:scale-110 transition-all duration-300 text-center"
                    style={{ color: currentColor }}>
                    {desafio.icon}
                  </div>
                  <h3 className="text-xl font-bold uppercase mb-4 tracking-wide transition-colors duration-300 text-center"
                    style={{ color: currentColor }}>
                    {desafio.title}
                  </h3>
                </div>

                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Call of Duty Black Ops 7 Section Component
const CODSection = () => {
  return (
    <section id="cod-black-ops-7" className="py-20 px-6 relative z-10 bg-gradient-to-b from-[#6bc2e2]/5 to-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Title */}
        <h2 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-[#6bc2e2] via-[#a126e9] to-[#ed3cb2] bg-clip-text text-transparent mb-8 uppercase leading-tight text-center">
          LANZAMIENTO<br />CALL OF DUTY BLACK OPS 7
        </h2>

        {/* Subtitle */}
        <div className="text-center mb-12">
          <p className="text-2xl md:text-3xl text-gray-300 font-light">
            ¡Vení a probar el nuevo Call of Duty antes que nadie!
          </p>
        </div>

        {/* Image - Full Width with Epic Styling */}
        <div className="relative group max-w-5xl mx-auto">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#6bc2e2]/40 to-[#a126e9]/40 rounded-3xl blur-3xl group-hover:blur-[80px] transition-all duration-700 opacity-60 group-hover:opacity-90"></div>

          {/* Main image container */}
          <div className="relative">
            <img
              src="/COD7.jpg"
              alt="Call of Duty Black Ops 7 - Lanzamiento Exclusivo"
              className="w-full h-auto rounded-3xl border-4 border-[#6bc2e2]/60 group-hover:border-[#a126e9] transition-all duration-500 relative z-10 shadow-2xl group-hover:shadow-[#6bc2e2]/50 group-hover:scale-[1.02]"
            />

            {/* Animated border frame */}
            <div className="absolute inset-0 rounded-3xl border-4 border-[#a126e9]/50 animate-pulse scale-[1.02] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

            {/* Corner accents - Larger and more prominent */}
            <div className="absolute top-6 left-6 w-16 h-16 border-l-4 border-t-4 border-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"></div>
            <div className="absolute top-6 right-6 w-16 h-16 border-r-4 border-t-4 border-[#ed3cb2] opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"></div>
            <div className="absolute bottom-6 left-6 w-16 h-16 border-l-4 border-b-4 border-[#a126e9] opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"></div>
            <div className="absolute bottom-6 right-6 w-16 h-16 border-r-4 border-b-4 border-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-all duration-500 group-hover:scale-110"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Cosplay Contest Section Component
const CosplaySection = () => {
  return (
    <section id="cosplay" className="py-20 px-6 relative z-10 bg-gradient-to-b from-transparent to-[#ed3cb2]/5">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content - Left Side */}
          <div className="order-2 lg:order-1">
            <div className="inline-block px-6 py-2 border-2 border-[#ed3cb2]/60 rounded-full bg-[#ed3cb2]/20 mb-6 backdrop-blur-sm">
              <span className="text-[#ed3cb2] font-bold uppercase tracking-wider">🎭 Evento Especial 🎭</span>
            </div>

            <h2 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-[#ed3cb2] via-[#a126e9] to-[#6bc2e2] bg-clip-text text-transparent mb-6 uppercase leading-tight">
              Concurso de Cosplay
            </h2>

            <div className="bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] rounded-2xl p-1 mb-8">
              <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a] rounded-xl p-6">
                <p className="text-3xl md:text-5xl font-black bg-gradient-to-r from-[#ed3cb2] via-[#a126e9] to-[#6bc2e2] bg-clip-text text-transparent text-center">
                  ¡U$S 2750 DÓLARES<br />EN PREMIOS!
                </p>
              </div>
            </div>

            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Muestra tu mejor interpretación de tus personajes favoritos y compite por increíbles premios.
              ¡No te pierdas esta oportunidad única!
            </p>

            <a
              href="https://tte.thotcomputacion.com.uy/concurso-de-cosplay-inscripciones/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-12 py-6 text-xl bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] text-white font-bold uppercase tracking-wider rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ed3cb2]/50 relative overflow-hidden group"
            >
              <span className="absolute inset-0 bg-gradient-to-r from-[#a126e9] to-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
              <span className="relative z-10">¡Inscribirse!</span>
            </a>
          </div>

          {/* Image - Right Side */}
          <div className="order-1 lg:order-2">
            <div className="relative group">
              {/* Glow effect */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#ed3cb2]/30 to-[#a126e9]/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-50 group-hover:opacity-75"></div>

              {/* Main image */}
              <div className="relative">
                <img
                  src="/cosplay-own.jpeg"
                  alt="Concurso de Cosplay THOT Gaming Fest"
                  className="w-full h-auto rounded-3xl border-2 border-[#ed3cb2]/50 group-hover:border-[#ed3cb2] transition-all duration-300 relative z-10 shadow-2xl"
                />

                {/* Animated border frame */}
                <div className="absolute inset-0 rounded-3xl border-2 border-[#6bc2e2]/50 animate-pulse scale-105 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                {/* Corner accents */}
                <div className="absolute top-4 left-4 w-12 h-12 border-l-4 border-t-4 border-[#ed3cb2] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-4 right-4 w-12 h-12 border-r-4 border-b-4 border-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Sponsors Section Component
const SponsorsSection = () => {
  // Función para mezclar el array aleatoriamente
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const mainSponsorsBase = [
    { name: 'Antec', logo: '/marcas_main/antec.png' },
    { name: 'Logitech', logo: '/marcas_main/logitech.png' },
    { name: 'Gigabyte', logo: '/marcas_main/gigabyte.png' },
    { name: 'Viewsonic', logo: '/marcas_main/viewsonic.png' },
    { name: 'Kingston', logo: '/marcas_main/kingston.png' }
  ];

  const [mainSponsors] = useState(() => shuffleArray(mainSponsorsBase));

  const sponsors = [
    { name: 'Antel', logo: '/marcas_secundarias/antel.png' },
    { name: 'Hiksemi', logo: '/marcas_secundarias/Hiksemi.png' },
    { name: 'Palit', logo: '/marcas_secundarias/Palit.png' },
    { name: 'Samsung', logo: '/marcas_secundarias/Samsung.png' },
    { name: 'Cougar', logo: '/marcas_secundarias/Cougar.png' },
    { name: 'MSI', logo: '/marcas_secundarias/MSI.png' },
    { name: 'Redragon', logo: '/marcas_secundarias/Redragon.png' },
    { name: 'UDE', logo: '/marcas_secundarias/ude.png' },
    { name: 'JBL', logo: '/marcas_secundarias/JBL.png' },
    { name: 'Deepcool', logo: '/marcas_secundarias/Deepcool.png' },
    { name: 'Razer', logo: '/marcas_secundarias/RAZER.png' },
    { name: 'AMD', logo: '/marcas_secundarias/amd.png' },
    { name: 'Nexxt', logo: '/marcas_secundarias/Nexxt.png' },
    { name: 'Maono', logo: '/marcas_secundarias/Maono.png' },
    { name: 'Govee', logo: '/marcas_secundarias/Govee.png' },
    { name: 'Monster', logo: '/marcas_secundarias/monster.png' }
  ];

  return (
    <section id="sponsors" className="py-20 px-6 bg-[#a126e9]/5 relative z-10">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-16 bg-gradient-to-r from-[#a126e9] to-[#6bc2e2] bg-clip-text text-transparent uppercase">
          Patrocinadores Oficiales
        </h2>
        
        {/* Main Sponsors Section */}
        <div className="mb-16">
          <div className="text-center mb-8">
            <div className="inline-block px-8 py-3 border-2 border-[#ed3cb2]/60 rounded-full bg-[#ed3cb2]/20 backdrop-blur-sm">
              <span className="text-[#ed3cb2] font-bold uppercase tracking-wider text-lg">⭐ Main Sponsors ⭐</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-8">
            {mainSponsors.map((sponsor, index) => (
              <div
                key={sponsor.name}
                className="bg-gradient-to-br from-[#ed3cb2]/20 to-[#a126e9]/20 backdrop-blur-lg border-2 border-[#ed3cb2]/50 rounded-2xl px-16 py-12 flex items-center justify-center transition-all duration-500 hover:scale-105 hover:bg-[#ed3cb2]/30 hover:border-[#ed3cb2] hover:shadow-2xl hover:shadow-[#ed3cb2]/40 group relative overflow-hidden w-full md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)]"
                style={{ animationDelay: `${index * 150}ms` }}
              >
                {/* Animated background gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#ed3cb2]/30 to-[#a126e9]/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>

                {/* Corner accents */}
                <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 border-[#6bc2e2]/50 group-hover:border-[#6bc2e2] transition-colors duration-300"></div>
                <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 border-[#ed3cb2]/50 group-hover:border-[#ed3cb2] transition-colors duration-300"></div>

                <div className="relative z-10 w-full">
                  <img
                    src={sponsor.logo}
                    alt={sponsor.name}
                    className="w-full h-auto max-h-32 object-contain filter brightness-0 invert opacity-90 group-hover:opacity-100 transition-opacity duration-300"
                  />
                </div>

                {/* Shimmer effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 rounded-2xl"></div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Regular Sponsors Section */}
        <div>
          <div className="text-center mb-8">
            <div className="inline-block px-6 py-2 border border-[#6bc2e2]/60 rounded-full bg-[#6bc2e2]/10 backdrop-blur-sm">
              <span className="text-[#6bc2e2] font-semibold uppercase tracking-wide">Sponsors Oficiales</span>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {sponsors.map((sponsor, index) => (
              <div
                key={sponsor.name}
                className="bg-white/10 backdrop-blur-lg border border-[#6bc2e2]/30 rounded-xl p-6 flex items-center justify-center transition-all duration-300 hover:scale-105 hover:bg-[#6bc2e2]/20 hover:border-[#6bc2e2] hover:shadow-lg hover:shadow-[#6bc2e2]/30 group w-[calc(50%-0.75rem)] md:w-[calc(33.333%-1rem)] lg:w-[calc(25%-1.125rem)]"
                style={{ animationDelay: `${(index + 5) * 100}ms` }}
              >
                <img
                  src={sponsor.logo}
                  alt={sponsor.name}
                  className="w-full h-auto max-h-16 object-contain filter brightness-0 invert opacity-80 group-hover:opacity-100 transition-opacity duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Registration Section Component
const RegistrationSection = ({ onOpenModal }) => {
  return (
    <section id="registro" className="py-20 px-6 relative z-10">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] bg-clip-text text-transparent uppercase">
          ¡No te lo pierdas!
        </h2>

        <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
          Vení a festejar nuestros 15 años y sumate al evento gamer más grande del país 🤩.
        </p>

        <div className="flex flex-col md:flex-row gap-4 justify-center">
          <a
            href="https://redtickets.uy/evento/THOT-15-ANOS/23852/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              if (window.fbq) {
                window.fbq('track', 'ComprarEntrada');
              }
            }}
            className="px-12 py-6 text-xl bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] text-white font-bold uppercase tracking-wider rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ed3cb2]/50 relative overflow-hidden group"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-[#a126e9] to-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></span>
            <span className="relative z-10">Comprar entradas</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Location Section Component
const LocationSection = () => {
  return (
    <section id="ubicacion" className="py-20 px-6 relative z-10 bg-gradient-to-b from-[#a126e9]/5 to-transparent">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-6xl font-bold text-center mb-8 bg-gradient-to-r from-[#6bc2e2] to-[#a126e9] bg-clip-text text-transparent uppercase leading-relaxed py-4 overflow-visible">
          ¿Dónde nos vemos?
        </h2>

        <div className="text-center mb-12">
          <div className="inline-block px-8 py-3 border-2 border-[#6bc2e2]/60 rounded-full bg-[#6bc2e2]/20 backdrop-blur-sm mb-4">
            <span className="text-[#6bc2e2] font-bold uppercase tracking-wider text-lg">📍 Centro de Eventos del LATU 📍</span>
          </div>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Av. Dra. María Luisa Saldún de Rodríguez 2037<br />
            11500 Montevideo, Departamento de Montevideo
          </p>
        </div>

        {/* Map Container */}
        <div className="relative group">
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#6bc2e2]/30 to-[#a126e9]/30 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-50 group-hover:opacity-75"></div>

          {/* Map iframe */}
          <div className="relative bg-white/5 backdrop-blur-lg border-2 border-[#6bc2e2]/50 rounded-3xl overflow-hidden group-hover:border-[#a126e9] transition-all duration-500 shadow-2xl group-hover:shadow-[#6bc2e2]/50">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d3273.088564892395!2d-56.08305688509447!3d-34.87883087010568!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zMzTCsDUyJzQzLjgiUyA1NsKwMDQnNDkuOSJX!5e0!3m2!1ses!2suy!4v1699999999999!5m2!1ses!2suy"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-3xl"
              title="Mapa del Centro de Eventos del LATU"
            ></iframe>

            {/* Corner accents */}
            <div className="absolute top-4 left-4 w-12 h-12 border-l-4 border-t-4 border-[#6bc2e2] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            <div className="absolute bottom-4 right-4 w-12 h-12 border-r-4 border-b-4 border-[#ed3cb2] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="text-center mt-8">
          <div className="inline-block bg-gradient-to-r from-[#ed3cb2]/20 to-[#a126e9]/20 rounded-2xl p-6 backdrop-blur-sm border border-[#6bc2e2]/30">
            <p className="text-[#6bc2e2] font-bold text-lg mb-2">🎮 Parque de Innovación del LATU 🎮</p>
            <p className="text-gray-300 text-sm">El mejor lugar para vivir la experiencia gamer definitiva</p>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer className="bg-black border-t border-pink-500 py-12 px-6 relative z-10">
      <div className="max-w-6xl mx-auto text-center">
        <p className="text-gray-400">
          &copy; 2025 THOT Computación. 15 años conectando gamers.
        </p>
      </div>
    </footer>
  );
};

// Free Ticket Modal Component
const FreeTicketModal = ({ isOpen, onClose }) => {
  // Detectar URL del backend automáticamente
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    cedula: '',
    email: '',
    celular: '',
    factura: null
  });
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, factura: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData(prev => ({ ...prev, factura: file }));
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formDataToSend = new FormData();
    formDataToSend.append('nombre', formData.nombre);
    formDataToSend.append('apellido', formData.apellido);
    formDataToSend.append('cedula', formData.cedula);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('celular', formData.celular);
    formDataToSend.append('factura', formData.factura);

    try {
      const response = await fetch(`${API_URL}/api/free-ticket`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error al enviar solicitud:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <div className="bg-gradient-to-br from-[#1a1a1a] to-[#2a2a2a] border-2 border-[#a126e9]/50 rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors z-10"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Modal Content */}
        <div className="p-8">
          {submitStatus === 'success' ? (
            /* Success Message */
            <div className="text-center py-12">
              <div className="mb-6">
                <div className="mx-auto w-20 h-20 bg-gradient-to-r from-green-400 to-green-600 rounded-full flex items-center justify-center animate-bounce">
                  <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-green-400 to-green-600 bg-clip-text text-transparent mb-4">
                ¡Solicitud Enviada!
              </h2>
              <p className="text-xl text-gray-300 mb-4">
                Tu solicitud ha sido recibida exitosamente
              </p>
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-6 mb-6">
                <p className="text-green-400 text-lg">
                  Revisaremos tu factura y te contactaremos pronto al email:<br />
                  <strong className="text-green-300">{formData.email}</strong>
                </p>
              </div>
              <button
                onClick={() => {
                  onClose();
                  setFormData({ nombre: '', apellido: '', cedula: '', email: '', celular: '', factura: null });
                  setPreview(null);
                  setSubmitStatus(null);
                }}
                className="px-8 py-4 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] text-white font-bold uppercase tracking-wider rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ed3cb2]/50"
              >
                Cerrar
              </button>
            </div>
          ) : (
            /* Form */
            <>
              <h2 className="text-3xl font-bold bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] bg-clip-text text-transparent mb-4">
                Entrada Gratis
              </h2>
              <p className="text-gray-300 mb-6">
                Obtené hasta dos entradas gratis canjeandola con una factura de THOT computación. Para solicitar dos entradas, deberás llenar el formulario dos veces y usar la misma foto de factura.
              </p>
              <p>
                ¡Completa tus datos, adjunta una foto de tu factura y recibí tu entrada gratis!
              </p>
              <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Nombre</label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-black/50 border border-[#a126e9]/30 rounded-lg text-white focus:border-[#a126e9] focus:outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Apellido</label>
                <input
                  type="text"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-black/50 border border-[#a126e9]/30 rounded-lg text-white focus:border-[#a126e9] focus:outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Número de Cédula</label>
              <input
                type="text"
                name="cedula"
                value={formData.cedula}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-black/50 border border-[#a126e9]/30 rounded-lg text-white focus:border-[#a126e9] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-black/50 border border-[#a126e9]/30 rounded-lg text-white focus:border-[#a126e9] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Celular</label>
              <input
                type="tel"
                name="celular"
                value={formData.celular}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 bg-black/50 border border-[#a126e9]/30 rounded-lg text-white focus:border-[#a126e9] focus:outline-none transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#6bc2e2] mb-2">Foto de la Factura</label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-all ${
                  isDragging
                    ? 'border-[#a126e9] bg-[#a126e9]/10'
                    : 'border-[#a126e9]/30 hover:border-[#a126e9]/50'
                }`}
              >
                <input
                  type="file"
                  id="factura"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                  required={!formData.factura}
                />

                {preview ? (
                  <div className="space-y-4">
                    <img src={preview} alt="Preview" className="max-h-48 mx-auto rounded-lg" />
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, factura: null }));
                        setPreview(null);
                      }}
                      className="text-[#ed3cb2] hover:text-[#ed3cb2]/80 transition-colors"
                    >
                      Cambiar imagen
                    </button>
                  </div>
                ) : (
                  <label htmlFor="factura" className="cursor-pointer">
                    <div className="text-[#6bc2e2] mb-2">
                      <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                    </div>
                    <p className="text-gray-300">
                      Arrastra tu imagen aquí o <span className="text-[#a126e9] underline">haz clic para seleccionar</span>
                    </p>
                  </label>
                )}
              </div>
            </div>

            {submitStatus === 'error' && (
              <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-red-400">
                Hubo un error al enviar la solicitud. Por favor, intenta nuevamente.
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-gradient-to-r from-[#ed3cb2] to-[#a126e9] text-white font-bold uppercase tracking-wider rounded-full transform transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-[#ed3cb2]/50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Enviando...' : 'Solicitar Entrada Gratis'}
            </button>
          </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#ed3cb2]/80 via-[#a126e9]/70 to-[#ed3cb2]/60 text-white font-['Oswald'] overflow-x-hidden">
      {/* Enhanced Particle System Background */}
      <Particles
        particleCount={250}
        particleColors={['#ed3cb2', '#a126e9', '#f248b8', '#9c2de7', '#e642b0', '#8f1de3', '#ea4fb5', '#9630ea', '#e556b3', '#9d35eb']}
        moveParticlesOnHover={true}
        particleHoverFactor={0.6}
        speed={0.15}
        particleSpread={12}
        particleBaseSize={120}
        alphaParticles={true}
        sizeRandomness={2}
        cameraDistance={18}
        disableRotation={false}
      />

      {/* Navigation */}
      <Navigation />

      {/* Hero Section */}
      <HeroSection onOpenModal={() => setIsModalOpen(true)} />

      {/* Special Guests Section - MORTE & FALKE */}
      <SpecialGuestsSection />

      {/* Features Section */}
      <FeaturesSection />

      {/* Call of Duty Black Ops 7 Section */}
      <CODSection />

      {/* Cosplay Contest Section */}
      <CosplaySection />

      {/* Zona de Experiencia Section */}
      <ZonaExperienciaSection />

      {/* Desafíos Section */}
      <DesafiosSection />

      {/* Sponsors Section */}
      <SponsorsSection />

      {/* Registration Section */}
      <RegistrationSection onOpenModal={() => setIsModalOpen(true)} />

      {/* Location Section */}
      <LocationSection />

      {/* Footer */}
      <Footer />

      {/* Free Ticket Modal */}
      <FreeTicketModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default App;